const createIconTextItem = (icon = "", title = "", description = "") => ({
  icon,
  title,
  description,
});

const createStat = (icon = "", value = "", label = "") => ({
  icon,
  value,
  label,
});

const createTimelineItem = (year = "", title = "", description = "") => ({
  year,
  title,
  description,
});

const createNumberedItem = (number = "", title = "", description = "") => ({
  number,
  title,
  description,
});

export const ABOUT_US_DEFAULTS = {
  hero: {
    quoteText: "Since 2017 • Transforming Lives Through Yoga",
    title: "Welcome to Hatha Yogashram",
    heroImage: "",
  },
  stats: [
    createStat("📅", "7+", "Years Excellence"),
    createStat("👥", "1000+", "Students Trained"),
    createStat("🌏", "35+", "Countries Reached"),
    createStat("⭐", "4.9/5", "Student Rating"),
  ],
  floatingCards: [
    createIconTextItem(
      "🏔️",
      "Sacred Location",
      "Based in Rishikesh, the world capital of yoga, on the banks of holy Ganges"
    ),
    createIconTextItem(
      "🎓",
      "Yoga Alliance Certified",
      "Internationally recognized certification programs registered with Yoga Alliance"
    ),
    createIconTextItem(
      "👨‍🏫",
      "Expert Teachers",
      "Learn from experienced masters with 15-25 years of teaching expertise"
    ),
  ],
  overview: {
    sectionLabel: "Our Story",
    sectionTitle: "About Hatha Yogashram",
    leadText:
      "Hatha Yogashram was founded in 2017 with a vision to bring authentic yoga education to seekers from around the world. Nestled in the spiritual heart of Rishikesh, India, we have grown to become one of the most reputed yoga institutes across the nation.",
    paragraphs: [
      "Our journey began with a simple mission: to preserve and share the ancient wisdom of yoga while making it accessible and relevant to modern practitioners. Over the years, we have trained more than 1,000 students from 35+ countries.",
      "We take pride in constantly reviewing and transforming our approach, ensuring that our curriculum remains authentic yet contemporary.",
    ],
    aboutImage: "",
    mantraSymbol: "ॐ",
    mantraText: "योगः चित्त-वृत्ति निरोधः",
    highlights: [
      createIconTextItem(
        "✨",
        "Traditional Wisdom",
        "Rooted in ancient Hatha Yog traditions and Patanjali's Yoga Sutras"
      ),
      createIconTextItem(
        "🌐",
        "Global Community",
        "Students from Europe, America, Asia, and beyond join our family"
      ),
      createIconTextItem(
        "🏆",
        "Award-Winning",
        "Recognized as Best Yoga School in North India (2021)"
      ),
    ],
  },
  missionVision: {
    missionTitle: "Our Mission",
    missionDescription:
      "Hatha Yogashram is an international affiliate partner institute providing underlying Yoga Alliance certified teacher training programs.",
    missionPoints: [
      "Develop a deep personal practice",
      "Master the art of teaching yoga authentically",
      "Integrate yogic philosophy into daily life",
      "Spread health, peace and spiritual awareness globally",
    ],
    visionTitle: "Our Vision",
    visionDescription:
      "The commitment of global well-being drives us to constantly innovate plans and strategies that help people achieve their health goals.",
    visionPoints: [
      "Yoga is recognized as a complete lifestyle, not just exercise",
      "Ancient wisdom is preserved and made accessible to all",
      "Communities support each other's wellness journeys",
      "Peace and mindfulness become universal practices",
      "Every individual discovers their inner potential through yoga",
    ],
  },
  values: {
    sectionLabel: "What We Stand For",
    sectionTitle: "Our Core Values",
    sectionDesc:
      "The principles that guide every aspect of our teaching and community",
    items: [
      createIconTextItem(
        "🙏",
        "Authenticity",
        "We preserve and teach traditional yoga in its purest form, honoring ancient wisdom while making it accessible to modern practitioners."
      ),
      createIconTextItem(
        "❤️",
        "Compassion",
        "Every student is treated with kindness, patience and understanding. We create a safe, supportive environment for transformation."
      ),
      createIconTextItem(
        "🌟",
        "Excellence",
        "We maintain the highest standards in teaching, curriculum design, and student support, ensuring transformative learning experiences."
      ),
      createIconTextItem(
        "🌍",
        "Community",
        "Building a global family of yogis who support each other's journey toward health, happiness and spiritual awakening."
      ),
      createIconTextItem(
        "📚",
        "Knowledge",
        "Continuous learning and sharing of yogic wisdom through comprehensive courses, workshops and retreats."
      ),
      createIconTextItem(
        "🧘",
        "Practice",
        "We emphasize daily practice, discipline and dedication - the foundation of true yogic transformation."
      ),
    ],
  },
  timeline: {
    sectionLabel: "Our Journey",
    sectionTitle: "Timeline of Excellence",
    sectionDesc: "From humble beginnings to international recognition",
    items: [
      createTimelineItem(
        2017,
        "Foundation",
        "Hatha Yogashram was established in the spiritual capital of Rishikesh"
      ),
      createTimelineItem(
        2018,
        "Yoga Alliance",
        "Received Yoga Alliance certification and trained 100+ students"
      ),
      createTimelineItem(
        2019,
        "Expansion",
        "Launched online yoga classes and international programs"
      ),
      createTimelineItem(
        2020,
        "Global Reach",
        "Adapted during pandemic, reached students in 25+ countries"
      ),
      createTimelineItem(
        2021,
        "Recognition",
        "Awarded Best Yoga School in North India"
      ),
      createTimelineItem(
        2022,
        "Innovation",
        "Introduced specialized programs: Yin, Kundalini, Prenatal Yoga"
      ),
      createTimelineItem(
        2023,
        "Excellence",
        "Trained 500+ certified yoga teachers worldwide"
      ),
      createTimelineItem(
        2024,
        "Leadership",
        "Established as one of India's most reputed yoga institutes"
      ),
    ],
  },
  whyChoose: {
    sectionLabel: "Your Best Choice",
    sectionTitle: "Why Choose Hatha Yogashram?",
    items: [
      createNumberedItem(
        "01",
        "Authentic Tradition",
        "Learn genuine Hatha Yog from teachers trained in traditional Himalayan lineages, preserving ancient wisdom."
      ),
      createNumberedItem(
        "02",
        "Sacred Location",
        "Study in Rishikesh, where the Ganges flows from the Himalayas - a place of immense spiritual energy and natural beauty."
      ),
      createNumberedItem(
        "03",
        "Experienced Faculty",
        "Our teachers have 15-25 years of experience and deep knowledge of yoga philosophy, anatomy, and practice."
      ),
      createNumberedItem(
        "04",
        "Small Batch Size",
        "We maintain small class sizes (maximum 15-20 students) ensuring personalized attention and proper guidance."
      ),
      createNumberedItem(
        "05",
        "Comprehensive Curriculum",
        "Our courses cover asanas, pranayama, meditation, philosophy, anatomy, teaching methodology, and more."
      ),
      createNumberedItem(
        "06",
        "International Recognition",
        "Yoga Alliance certified programs recognized worldwide, enabling you to teach globally after graduation."
      ),
      createNumberedItem(
        "07",
        "Affordable Pricing",
        "High-quality training at reasonable prices, including accommodation, meals, and all materials."
      ),
      createNumberedItem(
        "08",
        "Holistic Approach",
        "Beyond physical practice - we emphasize spiritual growth, mindfulness, and lifestyle transformation."
      ),
      createNumberedItem(
        "09",
        "Online & Offline",
        "Flexible learning options with both residential courses in Rishikesh and online classes for global students."
      ),
      createNumberedItem(
        "10",
        "Lifetime Support",
        "Join our global alumni community with continued guidance, resources, and networking opportunities."
      ),
      createNumberedItem(
        "11",
        "Proven Success",
        "Our graduates teach successfully worldwide, many running their own yoga studios and online classes."
      ),
      createNumberedItem(
        "12",
        "Cultural Immersion",
        "Experience authentic Indian yoga culture with temple visits, Ganga Aarti, and spiritual excursions."
      ),
    ],
  },
  goals: {
    sectionLabel: "Our Roadmap",
    sectionTitle: "Vision Goals",
    items: [
      createIconTextItem(
        "📢",
        "Creating Awareness",
        "Provide curated space through blogs, diet suggestions, recipes, and educational content to spread yoga knowledge and healthy lifestyle practices."
      ),
      createIconTextItem(
        "🎓",
        "Training Practitioners",
        "Offer array of training courses - from beginner to advanced - to boost confidence, skills and deep understanding of yoga."
      ),
      createIconTextItem(
        "🤝",
        "Mentoring Community",
        "Handpicked retreats, workshops and satsangs for rejuvenation, growth and building supportive yoga community worldwide."
      ),
      createIconTextItem(
        "👨‍🏫",
        "Platform for Teachers",
        "Create opportunities for experienced teachers to share their knowledge, expertise and unique perspectives with public."
      ),
      createIconTextItem(
        "🌈",
        "Variety of Styles",
        "Cater to different needs and preferences with Hatha, Ashtanga, Vinyasa, Yin, Kundalini and specialized yoga programs."
      ),
      createIconTextItem(
        "🧘",
        "Lifestyle Choice",
        "Promote yoga as a complete lifestyle encompassing physical health, mental peace, spiritual growth and conscious living."
      ),
    ],
  },
  facilities: {
    sectionLabel: "Our Ashram",
    sectionTitle: "World-Class Facilities",
    items: [
      createIconTextItem(
        "🏠",
        "Comfortable Accommodation",
        "Clean, peaceful rooms with attached bathrooms, AC options, and mountain or river views"
      ),
      createIconTextItem(
        "🍽️",
        "Nutritious Sattvic Meals",
        "Three vegetarian meals daily with fresh, organic ingredients prepared with love"
      ),
      createIconTextItem(
        "🧘‍♀️",
        "Spacious Yoga Halls",
        "Large, well-ventilated practice spaces with natural light and peaceful ambiance"
      ),
      createIconTextItem(
        "📚",
        "Yoga Library",
        "Extensive collection of yoga texts, scriptures, and reference materials"
      ),
      createIconTextItem(
        "🌿",
        "Beautiful Gardens",
        "Serene outdoor spaces perfect for meditation and relaxation"
      ),
      createIconTextItem(
        "🌐",
        "Free WiFi",
        "High-speed internet throughout the ashram for staying connected"
      ),
    ],
  },
  studentReviews: {
    sectionLabel: "Student Stories",
    sectionTitle: "What Our Students Say",
    sectionDesc:
      "Real feedback from our yoga community, shared through our live Google Reviews feed.",
  },
  cta: {
    title: "Begin Your Transformation Today",
    description:
      "Join thousands of students who have transformed their lives through authentic yoga education at Hatha Yogashram",
    primaryButtonText: "Explore Our Courses",
    secondaryButtonText: "Book a Free Consultation",
  },
};

export const ABOUT_US_EMPTY_STATE = () =>
  JSON.parse(JSON.stringify(ABOUT_US_DEFAULTS));
